import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const AccessibilityPage = () => {
  return (
    <div className="min-h-screen bg-[#050505] pt-20">
      {/* Decorative orange orbs */}
      <div className="orange-orb orange-orb-1 top-32 -left-32 w-[28rem] h-[28rem] z-0" />
      <div className="orange-orb orange-orb-2 top-1/2 -right-32 w-[32rem] h-[32rem] z-0" />
      <div className="relative z-10">
      <SEO
        title="Accessibility Statement"
        description="Agentic Atoms is committed to ensuring our website is accessible to all people, regardless of ability or technology."
        path="/accessibility"
      />
      <div className="max-w-4xl mx-auto px-6 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>

      <div className="max-w-4xl mx-auto px-6 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-testid="accessibility-title">
            Accessibility Statement
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <div className="bg-white/[0.03] rounded-2xl p-8 md:p-10 text-[#A1A1AA] leading-relaxed space-y-6">
            <h2 className="text-xl font-semibold text-white">Our Commitment</h2>
            <p>
              Agentic Atoms is committed to ensuring digital accessibility for people of all abilities. We continually improve the user experience for everyone and apply the relevant accessibility standards to achieve this.
            </p>

            <h2 className="text-xl font-semibold text-white">Standards We Follow</h2>
            <p>
              We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. These guidelines explain how to make web content more accessible to people with a wide range of disabilities, including visual, auditory, physical, speech, cognitive, language, learning, and neurological disabilities.
            </p>

            <h2 className="text-xl font-semibold text-white">What We Do</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide text alternatives for non-text content</li>
              <li>Ensure sufficient color contrast across all pages</li>
              <li>Make all functionality available from a keyboard</li>
              <li>Use clear, readable fonts and consistent navigation</li>
              <li>Structure content with proper headings and landmarks</li>
              <li>Ensure forms are labeled and provide clear error messages</li>
            </ul>

            <h2 className="text-xl font-semibold text-white">Feedback</h2>
            <p>
              We welcome your feedback on the accessibility of the Agentic Atoms website. If you encounter any accessibility barriers or have suggestions for improvement, please contact us:
            </p>
            <ul className="list-none space-y-1">
              <li><strong>Email:</strong>{' '}
                <a href="mailto:connect@agenticatoms.com" className="text-white underline font-medium">connect@agenticatoms.com</a>
              </li>
              <li><strong>Phone:</strong> +1-227-241-6372</li>
            </ul>
            <p>We aim to respond to accessibility feedback within 2 business days.</p>

            <p className="text-sm text-[#71717A] pt-4 border-t border-white/10">
              This statement was last updated on March 27, 2026.
            </p>
          </div>
        </motion.div>
      </div></div>
    </div>
  );
};

export default AccessibilityPage;
